import { Loader2, ArrowRight, Download, Compass, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { CoverageBar, CohortTags, RecChips } from './capabilityShared';

export function MemberDrawer({ open, onOpenChange, memberData, onOpenBrief, onDownloadUpskill, onFindInterventions }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cap-member-dialog">
        <DialogHeader>
          <DialogTitle>{memberData?.name || 'Loading…'}</DialogTitle>
          <DialogDescription className="sr-only">Member capability profile: assigned role coverage, skills, and succession fit.</DialogDescription>
        </DialogHeader>
        {!memberData ? (
          <div className="py-8 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <p className="text-xs text-slate-500 shrink-0">{memberData.department}</p>
                <CohortTags cohorts={memberData.cohorts} testId="cap-member-cohorts" max={4} />
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 border-cyan-200 text-cyan-700 hover:bg-cyan-50"
                  onClick={() => onOpenBrief({ mode: 'member', member_id: memberData.member_id }, `${memberData.name} — impact brief`)}
                  data-testid="cap-member-ai-brief">
                  <Sparkles className="h-3.5 w-3.5" /> AI impact brief
                </Button>
                {memberData.assigned_roles.some(r => !r.covered) && (
                  <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
                    onClick={() => onDownloadUpskill(memberData.member_id)}
                    data-testid="cap-member-upskill-plan">
                    <Download className="h-3.5 w-3.5" /> Upskilling plan
                  </Button>
                )}
                {memberData.assigned_roles.some(r => !r.covered) && (() => {
                  const uncovered = memberData.assigned_roles.find(r => !r.covered);
                  return (
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
                      onClick={() => onFindInterventions(memberData.name, uncovered?.role_name, uncovered?.gaps?.length, memberData.member_id, uncovered?.role_id)}
                      data-testid="cap-member-find-interventions">
                      <Compass className="h-3.5 w-3.5" /> Find interventions
                    </Button>
                  );
                })()}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Assigned Role Coverage</p>
              {memberData.assigned_roles.map(r => (
                <div key={r.role_id} className="rounded-lg border border-slate-200 p-3 mb-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-800">{r.role_name}</span>
                    {r.covered
                      ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Fully covered</Badge>
                      : <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{r.met_count}/{r.required_count}</Badge>}
                  </div>
                  <CoverageBar pct={r.coverage_pct} className="mt-2" />
                  {r.gaps.length > 0 && (
                    <p className="text-[11px] text-slate-500 mt-1.5">Gaps: {r.gaps.map(g => `${g.skill_name} (${g.current_level}→${g.target_level})`).join(', ')}</p>
                  )}
                  {r.recommended_interventions?.length > 0 && (
                    <RecChips items={r.recommended_interventions} testId={`cap-member-recs-${r.role_id}`} />
                  )}
                </div>
              ))}
            </div>

            {memberData.qualifies_for.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Could Fill (≥60% coverage)</p>
                {memberData.qualifies_for.map(r => (
                  <div key={r.role_id} className="flex items-center gap-2 text-sm py-1">
                    <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                    <span className="flex-1 text-slate-700 truncate">{r.role_name}</span>
                    {r.ready_now && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Ready</Badge>}
                    <span className="text-xs text-slate-500 tabular-nums">{r.coverage_pct}%</span>
                  </div>
                ))}
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Skill Profile ({memberData.skills.length})</p>
              <div className="grid grid-cols-1 gap-1">
                {memberData.skills.map(s => (
                  <div key={s.skill_id} className="flex items-center gap-2 text-sm">
                    <span className="flex-1 text-slate-700 truncate">{s.skill_name}</span>
                    <span className="text-[10px] text-slate-400">{s.skill_family}</span>
                    <span className="text-xs font-semibold text-slate-700 tabular-nums w-6 text-right">{s.current_level}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
